import { useState, useEffect } from 'react'
import { Node, Edge } from '@xyflow/react'
import { Paintbrush, Type, Layers, Database, ChevronDown, ChevronRight } from 'lucide-react'
import '../../styles/properties-panel-enhanced.css'

interface PropertiesPanelEnhancedProps {
  selectedNodes: Node[]
  selectedEdges: Edge[]
  allNodes: Node[]
  onNodeUpdate: (nodeId: string, updates: Partial<Node>) => void
  onNodesUpdate?: (ids: string[], dataUpdates: Record<string, any>) => void
  onEdgeUpdate: (edgeId: string, updates: Partial<Edge>) => void
  onSelectNode?: (nodeId: string) => void
  onGroupNodes?: () => void
  onUngroupNodes?: () => void
  onClose?: () => void
}

type Tab = 'style' | 'text' | 'arrange' | 'data'

// Sentinel returned by commonValue() when the selected nodes disagree on a value.
const MIXED = Symbol('mixed-value')
type Maybe<T> = T | typeof MIXED

/**
 * Returns the value shared by every node (via getKey), or the MIXED sentinel
 * when the values differ. Used by the multi-select editors to drive a clearly
 * indicated "Mixed" state on each control.
 */
function commonValue<T>(nodes: Node[], getKey: (n: Node) => T): Maybe<T> {
  if (nodes.length === 0) return MIXED
  const first = getKey(nodes[0])
  for (let i = 1; i < nodes.length; i++) {
    if (getKey(nodes[i]) !== first) return MIXED
  }
  return first
}

export default function PropertiesPanelEnhanced({
  selectedNodes,
  selectedEdges,
  allNodes,
  onNodeUpdate,
  onNodesUpdate,
  onEdgeUpdate,
  onSelectNode,
  onGroupNodes,
  onUngroupNodes,
  onClose
}: PropertiesPanelEnhancedProps) {
  const [activeTab, setActiveTab] = useState<Tab>('style')
  const [localNode, setLocalNode] = useState<Node | null>(null)

  // Update local node when selection changes (fixes the bug!)
  useEffect(() => {
    if (selectedNodes.length === 1) {
      setLocalNode(selectedNodes[0])
    } else {
      setLocalNode(null)
    }
  }, [selectedNodes])

  const node = localNode
  const edge = selectedEdges.length === 1 ? selectedEdges[0] : null

  // Multi-selection: bulk styling across all selected nodes with mixed-value state
  if (selectedNodes.length > 1) {
    // Fan out a data change to every selected node (undoable via onNodesUpdate,
    // falling back to per-node onNodeUpdate).
    const updateAllData = (dataUpdates: Record<string, any>) => {
      const ids = selectedNodes.map((n) => n.id)
      if (onNodesUpdate) {
        onNodesUpdate(ids, dataUpdates)
      } else {
        selectedNodes.forEach((n) => onNodeUpdate(n.id, { data: { ...n.data, ...dataUpdates } }))
      }
    }

    return (
      <div className="properties-panel-enhanced">
        <div className="panel-header">
          <h3>Properties</h3>
          {onClose && <button onClick={onClose} className="close-btn">×</button>}
        </div>

        <div className="panel-tabs">
          <button
            className={activeTab === 'style' ? 'active' : ''}
            onClick={() => setActiveTab('style')}
            title="Style properties"
          >
            <Paintbrush size={16} />
            <span>Style</span>
          </button>
          <button
            className={activeTab === 'text' ? 'active' : ''}
            onClick={() => setActiveTab('text')}
            title="Text properties"
          >
            <Type size={16} />
            <span>Text</span>
          </button>
        </div>

        <div className="panel-content">
          <p className="multi-select-info">
            {selectedNodes.length} nodes selected — changes apply to all
          </p>
          {(activeTab === 'text' ? (
            <MultiTextTab nodes={selectedNodes} updateAllData={updateAllData} />
          ) : (
            <MultiStyleTab nodes={selectedNodes} updateAllData={updateAllData} />
          ))}
        </div>
      </div>
    )
  }

  // No selection
  if (!node && !edge) {
    return (
      <div className="properties-panel-enhanced">
        <div className="panel-header">
          <h3>Properties</h3>
          {onClose && <button onClick={onClose} className="close-btn">×</button>}
        </div>
        <div className="panel-content">
          <div className="no-selection">
            <div className="no-selection-icon" aria-hidden>
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1.5" />
                <rect x="14" y="14" width="7" height="7" rx="1.5" />
                <path d="M10 6.5h2.5a2 2 0 0 1 2 2V14" />
              </svg>
            </div>
            <p className="no-selection-title">Nothing selected</p>
            <p className="no-selection-hint">Click a node or edge on the canvas to edit its style, content, and connections.</p>
          </div>
        </div>
      </div>
    )
  }

  // Edge selected - show edge properties
  if (edge && !node) {
    return (
      <div className="properties-panel-enhanced">
        <div className="panel-header">
          <h3>Connector Properties</h3>
          {onClose && <button onClick={onClose} className="close-btn">×</button>}
        </div>
        <div className="panel-content">
          <EdgePropertiesTab edge={edge} onEdgeUpdate={onEdgeUpdate} />
        </div>
      </div>
    )
  }

  const updateNode = (updates: any) => {
    if (!node) return
    const newNode = { ...node, ...updates }
    setLocalNode(newNode)
    onNodeUpdate(node.id, updates)
  }

  const updateNodeData = (dataUpdates: any) => {
    if (!node) return
    const newData = { ...node.data, ...dataUpdates }
    updateNode({ data: newData })
  }

  const updateNodeStyle = (styleUpdates: any) => {
    if (!node) return
    const newStyle = { ...node.style, ...styleUpdates }
    updateNode({ style: newStyle })
  }

  return (
    <div className="properties-panel-enhanced">
      <div className="panel-header">
        <h3>Properties</h3>
        {onClose && <button onClick={onClose} className="close-btn">×</button>}
      </div>

      {/* Tabs */}
      <div className="panel-tabs">
        <button
          className={activeTab === 'style' ? 'active' : ''}
          onClick={() => setActiveTab('style')}
          title="Style properties"
        >
          <Paintbrush size={16} />
          <span>Style</span>
        </button>
        <button
          className={activeTab === 'text' ? 'active' : ''}
          onClick={() => setActiveTab('text')}
          title="Text properties"
        >
          <Type size={16} />
          <span>Text</span>
        </button>
        <button
          className={activeTab === 'arrange' ? 'active' : ''}
          onClick={() => setActiveTab('arrange')}
          title="Arrange & position"
        >
          <Layers size={16} />
          <span>Arrange</span>
        </button>
        <button
          className={activeTab === 'data' ? 'active' : ''}
          onClick={() => setActiveTab('data')}
          title="Node data"
        >
          <Database size={16} />
          <span>Data</span>
        </button>
      </div>

      {/* Content */}
      <div className="panel-content">
        {activeTab === 'style' && node && (
          <StyleTab node={node} updateNode={updateNode} updateStyle={updateNodeStyle} updateData={updateNodeData} />
        )}
        {activeTab === 'text' && node && (
          <TextTab node={node} updateData={updateNodeData} />
        )}
        {activeTab === 'arrange' && node && (
          <ArrangeTab
            node={node}
            allNodes={allNodes}
            updateNode={updateNode}
            onSelectNode={onSelectNode}
            onGroupNodes={onGroupNodes}
            onUngroupNodes={onUngroupNodes}
          />
        )}
        {activeTab === 'data' && node && (
          <DataTab node={node} updateData={updateNodeData} />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// MULTI-SELECT (BULK) STYLING
// ---------------------------------------------------------------------------

const isMixed = (v: any): v is typeof MIXED => v === MIXED

// A color control (swatch + hex text) that shows a clearly-indicated Mixed
// state (empty value + "Mixed" placeholder) when selected nodes disagree.
function MixedColorRow({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string
  value: Maybe<string | undefined>
  fallback: string
  onChange: (v: string) => void
}) {
  const mixed = isMixed(value)
  const resolved = mixed ? '' : (value ?? '')
  return (
    <PropertyRow label={label}>
      <input
        type="color"
        value={mixed || !resolved ? fallback : resolved}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '40px', height: '32px', flexShrink: 0 }}
      />
      <input
        type="text"
        value={resolved}
        onChange={(e) => onChange(e.target.value)}
        className="color-text"
        placeholder={mixed ? 'Mixed' : fallback}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: '13px',
          padding: '6px 8px',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
        }}
      />
    </PropertyRow>
  )
}

// MULTI STYLE TAB — fill, stroke, opacity across all selected nodes
function MultiStyleTab({ nodes, updateAllData }: { nodes: Node[]; updateAllData: (u: Record<string, any>) => void }) {
  const bgColor = commonValue(nodes, (n) => (n.data as any)?.bgColor)
  const fillOpacity = commonValue(nodes, (n) => (n.data as any)?.fillOpacity ?? 1)
  const borderColor = commonValue(nodes, (n) => (n.data as any)?.borderColor)
  const borderWidth = commonValue(nodes, (n) => (n.data as any)?.borderWidth ?? 2)
  const borderStyle = commonValue(nodes, (n) => (n.data as any)?.borderStyle ?? 'solid')

  return (
    <div className="tab-content">
      <Section title="Fill">
        <MixedColorRow
          label="Color"
          value={bgColor as Maybe<string>}
          fallback="#3b82f6"
          onChange={(v) => updateAllData({ bgColor: v })}
        />
        <PropertyRow label="Opacity">
          <input
            type="range"
            min="0"
            max="100"
            value={isMixed(fillOpacity) ? 100 : Math.round((fillOpacity as number) * 100)}
            onChange={(e) => updateAllData({ fillOpacity: parseInt(e.target.value) / 100 })}
            style={{ flex: 1 }}
          />
          <span style={{ minWidth: '45px', textAlign: 'right', fontSize: '13px', color: '#374151' }}>
            {isMixed(fillOpacity) ? 'Mixed' : `${Math.round((fillOpacity as number) * 100)}%`}
          </span>
        </PropertyRow>
      </Section>

      <Section title="Stroke">
        <MixedColorRow
          label="Color"
          value={borderColor as Maybe<string>}
          fallback="#1e293b"
          onChange={(v) => updateAllData({ borderColor: v })}
        />
        <PropertyRow label="Width">
          <input
            type="range"
            min="0"
            max="10"
            step="0.5"
            value={isMixed(borderWidth) ? 2 : parseFloat(String(borderWidth))}
            onChange={(e) => updateAllData({ borderWidth: parseFloat(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{ minWidth: '45px', textAlign: 'right', fontSize: '13px', color: '#374151' }}>
            {isMixed(borderWidth) ? 'Mixed' : `${parseFloat(String(borderWidth))}px`}
          </span>
        </PropertyRow>
        <PropertyRow label="Style">
          <select
            value={isMixed(borderStyle) ? '' : (borderStyle as string)}
            onChange={(e) => updateAllData({ borderStyle: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
            }}
          >
            {isMixed(borderStyle) && <option value="" disabled>Mixed</option>}
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
          </select>
        </PropertyRow>
      </Section>
    </div>
  )
}

// MULTI TEXT TAB — font + alignment + color across all selected nodes
function MultiTextTab({ nodes, updateAllData }: { nodes: Node[]; updateAllData: (u: Record<string, any>) => void }) {
  const fontFamily = commonValue(nodes, (n) => (n.data as any)?.fontFamily ?? 'Inter')
  const fontSize = commonValue(nodes, (n) => (n.data as any)?.fontSize ?? 14)
  const fontWeight = commonValue(nodes, (n) => (n.data as any)?.fontWeight ?? '400')
  const textColor = commonValue(nodes, (n) => (n.data as any)?.textColor)
  const textAlign = commonValue(nodes, (n) => (n.data as any)?.textAlign ?? 'left')

  return (
    <div className="tab-content">
      <Section title="Font">
        <PropertyRow label="Family">
          <select
            value={isMixed(fontFamily) ? '' : (fontFamily as string)}
            onChange={(e) => updateAllData({ fontFamily: e.target.value })}
          >
            {isMixed(fontFamily) && <option value="" disabled>Mixed</option>}
            <option value="Inter">Inter</option>
            <option value="Arial">Arial</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Size">
          <input
            type="number"
            min="8"
            max="96"
            value={isMixed(fontSize) ? '' : (fontSize as number)}
            placeholder={isMixed(fontSize) ? 'Mixed' : ''}
            onChange={(e) => updateAllData({ fontSize: parseInt(e.target.value) })}
            className="number-input"
          />
          <span>px</span>
        </PropertyRow>
        <PropertyRow label="Weight">
          <select
            value={isMixed(fontWeight) ? '' : (fontWeight as string)}
            onChange={(e) => updateAllData({ fontWeight: e.target.value })}
          >
            {isMixed(fontWeight) && <option value="" disabled>Mixed</option>}
            <option value="300">Light</option>
            <option value="400">Regular</option>
            <option value="500">Medium</option>
            <option value="600">Semi Bold</option>
            <option value="700">Bold</option>
          </select>
        </PropertyRow>
        <MixedColorRow
          label="Color"
          value={textColor as Maybe<string>}
          fallback="#1e293b"
          onChange={(v) => updateAllData({ textColor: v })}
        />
      </Section>

      <Section title="Alignment">
        <PropertyRow label="Horizontal">
          <div className="button-group">
            <button
              className={!isMixed(textAlign) && textAlign === 'left' ? 'active' : ''}
              onClick={() => updateAllData({ textAlign: 'left' })}
            >
              Left
            </button>
            <button
              className={!isMixed(textAlign) && textAlign === 'center' ? 'active' : ''}
              onClick={() => updateAllData({ textAlign: 'center' })}
            >
              Center
            </button>
            <button
              className={!isMixed(textAlign) && textAlign === 'right' ? 'active' : ''}
              onClick={() => updateAllData({ textAlign: 'right' })}
            >
              Right
            </button>
          </div>
        </PropertyRow>
      </Section>
    </div>
  )
}

// STYLE TAB
function StyleTab({ node, updateNode, updateData }: any) {
  const style = node.style || {}
  const data = node.data || {}

  return (
    <div className="tab-content">
      {/* Fill */}
      <Section title="Fill">
        <PropertyRow label="Enabled">
          <input
            type="checkbox"
            checked={data.bgColor !== 'transparent'}
            onChange={(e) => {
              if (e.target.checked) {
                updateData({ bgColor: data.bgColor === 'transparent' ? '#3b82f6' : (data.bgColor || '#3b82f6') })
              } else {
                updateData({ bgColor: 'transparent' })
              }
            }}
          />
          <span style={{ marginLeft: '8px' }}>
            {data.bgColor === 'transparent' ? 'Off' : 'On'}
          </span>
        </PropertyRow>
        {data.bgColor !== 'transparent' && (
          <PropertyRow label="Color">
            <input
              type="color"
              value={data.bgColor || style.backgroundColor || '#3b82f6'}
              onChange={(e) => updateData({ bgColor: e.target.value })}
              style={{ width: '40px', height: '32px', flexShrink: 0 }}
            />
            <input
              type="text"
              value={data.bgColor || style.backgroundColor || '#3b82f6'}
              onChange={(e) => updateData({ bgColor: e.target.value })}
              className="color-text"
              placeholder="#3b82f6"
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
          </PropertyRow>
        )}
        <PropertyRow label="Opacity">
          <input
            type="range"
            min="0"
            max="100"
            value={(data.fillOpacity !== undefined ? data.fillOpacity : (style.opacity !== undefined ? style.opacity : 1)) * 100}
            onChange={(e) => updateData({ fillOpacity: parseInt(e.target.value) / 100 })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {Math.round((data.fillOpacity !== undefined ? data.fillOpacity : (style.opacity !== undefined ? style.opacity : 1)) * 100)}%
          </span>
        </PropertyRow>
      </Section>

      {/* Header Color - Only for structured nodes and containers */}
      {(node.type === 'database-table' || node.type === 'api-resource' || node.type === 'api-endpoint' || node.type === 'uml-class' || node.type === 'group-container') && (
        <Section title="Header Color">
          <PropertyRow label="Background">
            <input
              type="color"
              value={data.headerBgColor || '#0891b2'}
              onChange={(e) => updateData({ headerBgColor: e.target.value })}
              style={{ width: '40px', height: '32px', flexShrink: 0 }}
            />
            <input
              type="text"
              value={data.headerBgColor || '#0891b2'}
              onChange={(e) => updateData({ headerBgColor: e.target.value })}
              className="color-text"
              placeholder="#0891b2"
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
          </PropertyRow>
          <PropertyRow label="Text">
            <input
              type="color"
              value={data.headerTextColor || '#ffffff'}
              onChange={(e) => updateData({ headerTextColor: e.target.value })}
              style={{ width: '40px', height: '32px', flexShrink: 0 }}
            />
            <input
              type="text"
              value={data.headerTextColor || '#ffffff'}
              onChange={(e) => updateData({ headerTextColor: e.target.value })}
              className="color-text"
              placeholder="#ffffff"
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
          </PropertyRow>
        </Section>
      )}

      {/* Stroke */}
      <Section title="Stroke">
        <PropertyRow label="Enabled">
          <input
            type="checkbox"
            checked={data.borderWidth !== 0}
            onChange={(e) => {
              if (e.target.checked) {
                updateData({ borderWidth: 2, borderColor: data.borderColor || '#1e293b' })
              } else {
                updateData({ borderWidth: 0 })
              }
            }}
          />
          <span style={{ marginLeft: '8px' }}>
            {data.borderWidth === 0 ? 'Off' : 'On'}
          </span>
        </PropertyRow>
        {data.borderWidth !== 0 && (
          <>
            <PropertyRow label="Color">
              <input
                type="color"
                value={data.borderColor || style.borderColor || '#1e293b'}
                onChange={(e) => updateData({ borderColor: e.target.value })}
                style={{ width: '40px', height: '32px', flexShrink: 0 }}
              />
              <input
                type="text"
                value={data.borderColor || style.borderColor || '#1e293b'}
                onChange={(e) => updateData({ borderColor: e.target.value })}
                className="color-text"
                placeholder="Type 'transparent' or hex"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
            </PropertyRow>
            <PropertyRow label="Width">
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={parseFloat(String(data.borderWidth || 2))}
                onChange={(e) => updateData({ borderWidth: parseFloat(e.target.value) })}
                style={{ flex: 1 }}
              />
              <span style={{
                minWidth: '45px',
                textAlign: 'right',
                fontSize: '13px',
                color: '#374151'
              }}>
                {parseFloat(String(data.borderWidth || 2))}px
              </span>
            </PropertyRow>
          </>
        )}
        <PropertyRow label="Style">
          <select
            value={data.borderStyle || style.borderStyle || 'solid'}
            onChange={(e) => updateData({ borderStyle: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
          </select>
        </PropertyRow>
      </Section>

      {/* Shadow */}
      <Section title="Shadow">
        <PropertyRow label="Enabled">
          <input
            type="checkbox"
            checked={data.shadow === true}
            onChange={(e) => updateData({ shadow: e.target.checked })}
          />
          <span style={{ marginLeft: '8px' }}>{data.shadow === true ? 'On' : 'Off'}</span>
        </PropertyRow>
        {data.shadow === true && (
          <>
            <PropertyRow label="Blur">
              <input
                type="range"
                min="0"
                max="20"
                value={data.shadowBlur || 4}
                onChange={(e) => updateData({ shadowBlur: parseInt(e.target.value) })}
              />
              <span>{data.shadowBlur || 4}px</span>
            </PropertyRow>
          </>
        )}
      </Section>

      {/* Size & Position */}
      <Section title="Size & Position">
        <PropertyRow label="X">
          <input
            type="number"
            value={Math.round(node.position.x)}
            onChange={(e) => updateNode({ position: { ...node.position, x: parseInt(e.target.value) } })}
            className="number-input"
          />
        </PropertyRow>
        <PropertyRow label="Y">
          <input
            type="number"
            value={Math.round(node.position.y)}
            onChange={(e) => updateNode({ position: { ...node.position, y: parseInt(e.target.value) } })}
            className="number-input"
          />
        </PropertyRow>
        <PropertyRow label="Width">
          <input
            type="number"
            value={Math.round(node.width || node.measured?.width || style.width || node.data?.resizedWidth || 200)}
            onChange={(e) => {
              const w = parseInt(e.target.value)
              if (!Number.isFinite(w) || w <= 0) return
              // Write what the node actually reads for size (node.width / data.resizedWidth,
              // mirroring the NodeResizer) — not style.width, which RF overrides.
              updateNode({
                width: w,
                style: { ...style, width: w },
                data: { ...data, resizedWidth: w },
              })
            }}
            className="number-input"
          />
        </PropertyRow>
        <PropertyRow label="Height">
          <input
            type="number"
            value={Math.round(node.height || node.measured?.height || style.height || node.data?.resizedHeight || 100)}
            onChange={(e) => {
              const h = parseInt(e.target.value)
              if (!Number.isFinite(h) || h <= 0) return
              updateNode({
                height: h,
                style: { ...style, height: h },
                data: { ...data, resizedHeight: h },
              })
            }}
            className="number-input"
          />
        </PropertyRow>
      </Section>
    </div>
  )
}

// TEXT TAB
function TextTab({ node, updateData }: any) {
  const data = node.data || {}
  const [showIconPicker, setShowIconPicker] = useState(false)

  // Check if this is a shape node - shapes shouldn't show icon field
  const isShapeNode = node.type === 'shape' || data.isShape || data.shapeId

  // Tech icons presets
  const techIcons = [
    { emoji: '⚛️', name: 'React' },
    { emoji: '🔷', name: 'TypeScript' },
    { emoji: '🟢', name: 'Node.js' },
    { emoji: '🐍', name: 'Python' },
    { emoji: '☕', name: 'Java' },
    { emoji: '🗄️', name: 'Database' },
    { emoji: '🔐', name: 'Security' },
    { emoji: '☁️', name: 'Cloud' },
    { emoji: '🚀', name: 'Deploy' },
    { emoji: '⚙️', name: 'Config' },
    { emoji: '📊', name: 'Analytics' },
    { emoji: '🔔', name: 'Notification' },
    { emoji: '📱', name: 'Mobile' },
    { emoji: '💻', name: 'Desktop' },
    { emoji: '🌐', name: 'Web' },
    { emoji: '🔌', name: 'API' },
    { emoji: '📦', name: 'Package' },
    { emoji: '🛠️', name: 'Tools' },
    { emoji: '🔄', name: 'Sync' },
    { emoji: '⚡', name: 'Performance' },
  ]

  return (
    <div className="tab-content">
      <Section title="Content">
        <PropertyRow label="Label">
          <textarea
            value={data.label || ''}
            onChange={(e) => updateData({ label: e.target.value })}
            rows={3}
            className="textarea-input"
          />
        </PropertyRow>

        {/* Only show Icon field for non-shape nodes */}
        {!isShapeNode && (
          <div className="property-row" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px',
            minHeight: '32px'
          }}>
            <label style={{
              minWidth: '80px',
              fontSize: '13px',
              color: '#6b7280',
              flexShrink: 0
            }}>Icon</label>
            <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
              <div style={{
                width: '36px',
                height: '36px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                flexShrink: 0
              }}>
                {data.icon?.startsWith('/icons/') || data.icon?.startsWith('/shapes/') ? (
                  <img src={data.icon} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                ) : (
                  <span style={{ fontSize: '20px' }}>{data.icon || '○'}</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                <button onClick={() => setShowIconPicker(!showIconPicker)} style={{
                  padding: '6px',
                  background: showIconPicker ? '#3b82f6' : 'white',
                  color: showIconPicker ? 'white' : '#374151',
                  border: `1px solid ${showIconPicker ? '#3b82f6' : '#d1d5db'}`,
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}>
                  {showIconPicker ? '✕ Close' : '✎ Change'}
                </button>
                {data.icon && (
                  <button onClick={() => updateData({ icon: '' })} style={{
                    padding: '6px',
                    background: 'white',
                    color: '#dc2626',
                    border: '1px solid #fecaca',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}>
                    🗑 Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {!isShapeNode && showIconPicker && (
          <div style={{
            padding: '10px',
            background: '#f9fafb',
            borderRadius: '6px',
            border: '1px solid #e5e7eb',
            marginTop: '8px'
          }}>
            {/* Restore to Default Button - Only show if icon was changed from original */}
            {(() => {
              // Use stored originalIcon reference instead of reconstructing path
              const originalIcon = data.originalIcon || ''
              const hasOriginalIcon = Boolean(originalIcon)
              const iconWasChanged = hasOriginalIcon && data.icon !== originalIcon

              return iconWasChanged ? (
                <button
                  onClick={() => {
                    updateData({ icon: originalIcon })
                    setShowIconPicker(false)
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    marginBottom: '10px',
                    border: '1px solid #3b82f6',
                    borderRadius: '6px',
                    background: '#eff6ff',
                    color: '#3b82f6',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#dbeafe'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#eff6ff'
                  }}
                >
                  ↺ Restore Default
                </button>
              ) : null
            })()}

            {/* Icon Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '6px',
              maxHeight: '180px',
              overflowY: 'auto'
            }}>
              {techIcons.map((icon, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    updateData({ icon: icon.emoji })
                    setShowIconPicker(false)
                  }}
                  style={{
                    padding: '6px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    background: 'white',
                    fontSize: '22px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    aspectRatio: '1'
                  }}
                  title={icon.name}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#eff6ff'
                    e.currentTarget.style.borderColor = '#3b82f6'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white'
                    e.currentTarget.style.borderColor = '#d1d5db'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                >
                  {icon.emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section title="Font">
        <PropertyRow label="Family">
          <select
            value={data.fontFamily || 'Inter'}
            onChange={(e) => updateData({ fontFamily: e.target.value })}
          >
            <option value="Inter">Inter</option>
            <option value="Arial">Arial</option>
            <option value="Helvetica">Helvetica</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Courier New">Courier New</option>
            <option value="Georgia">Georgia</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Size">
          <input
            type="number"
            min="8"
            max="96"
            value={data.fontSize || 14}
            onChange={(e) => updateData({ fontSize: parseInt(e.target.value) })}
            className="number-input"
          />
          <span>px</span>
        </PropertyRow>
        <PropertyRow label="Weight">
          <select
            value={data.fontWeight || '400'}
            onChange={(e) => updateData({ fontWeight: e.target.value })}
          >
            <option value="300">Light</option>
            <option value="400">Regular</option>
            <option value="500">Medium</option>
            <option value="600">Semi Bold</option>
            <option value="700">Bold</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Color">
          <input
            type="color"
            value={data.textColor || '#1e293b'}
            onChange={(e) => updateData({ textColor: e.target.value })}
          />
          <input
            type="text"
            value={data.textColor || '#1e293b'}
            onChange={(e) => updateData({ textColor: e.target.value })}
            className="color-text"
          />
        </PropertyRow>
      </Section>

      <Section title="Alignment">
        <PropertyRow label="Horizontal">
          <div className="button-group">
            <button
              className={data.textAlign === 'left' || !data.textAlign ? 'active' : ''}
              onClick={() => updateData({ textAlign: 'left' })}
            >
              Left
            </button>
            <button
              className={data.textAlign === 'center' ? 'active' : ''}
              onClick={() => updateData({ textAlign: 'center' })}
            >
              Center
            </button>
            <button
              className={data.textAlign === 'right' ? 'active' : ''}
              onClick={() => updateData({ textAlign: 'right' })}
            >
              Right
            </button>
          </div>
        </PropertyRow>
        <PropertyRow label="Vertical">
          <div className="button-group">
            <button
              className={data.verticalAlign === 'top' || !data.verticalAlign ? 'active' : ''}
              onClick={() => updateData({ verticalAlign: 'top' })}
            >
              Top
            </button>
            <button
              className={data.verticalAlign === 'middle' ? 'active' : ''}
              onClick={() => updateData({ verticalAlign: 'middle' })}
            >
              Middle
            </button>
            <button
              className={data.verticalAlign === 'bottom' ? 'active' : ''}
              onClick={() => updateData({ verticalAlign: 'bottom' })}
            >
              Bottom
            </button>
          </div>
        </PropertyRow>
      </Section>

      <Section title="Text Style">
        <PropertyRow label="Line Height">
          <input
            type="range"
            min="1"
            max="3"
            step="0.1"
            value={data.lineHeight || 1.5}
            onChange={(e) => updateData({ lineHeight: parseFloat(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {(data.lineHeight || 1.5).toFixed(1)}
          </span>
        </PropertyRow>
        <PropertyRow label="Decoration">
          <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px'
            }}>
              <input
                type="checkbox"
                checked={data.textDecoration?.includes('underline') || false}
                onChange={(e) => {
                  const current = data.textDecoration || ''
                  const newValue = e.target.checked
                    ? current ? `${current} underline` : 'underline'
                    : current.replace('underline', '').trim()
                  updateData({ textDecoration: newValue || 'none' })
                }}
              />
              Underline
            </label>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px'
            }}>
              <input
                type="checkbox"
                checked={data.textDecoration?.includes('line-through') || false}
                onChange={(e) => {
                  const current = data.textDecoration || ''
                  const newValue = e.target.checked
                    ? current ? `${current} line-through` : 'line-through'
                    : current.replace('line-through', '').trim()
                  updateData({ textDecoration: newValue || 'none' })
                }}
              />
              Strike
            </label>
          </div>
        </PropertyRow>
        <PropertyRow label="Word Wrap">
          <input
            type="checkbox"
            checked={data.wordWrap !== false}
            onChange={(e) => updateData({ wordWrap: e.target.checked })}
          />
          <span style={{ marginLeft: '8px', fontSize: '13px' }}>
            {data.wordWrap !== false ? 'Enabled' : 'Disabled'}
          </span>
        </PropertyRow>
        <PropertyRow label="Overflow">
          <select
            value={data.textOverflow || 'clip'}
            onChange={(e) => updateData({ textOverflow: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="visible">Visible (overflow)</option>
            <option value="hidden">Hidden (cut off)</option>
            <option value="clip">Clip (strict cut)</option>
            <option value="ellipsis">Ellipsis (...)</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Padding">
          <input
            type="range"
            min="0"
            max="32"
            step="2"
            value={data.textPadding || 12}
            onChange={(e) => updateData({ textPadding: parseInt(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {data.textPadding || 12}px
          </span>
        </PropertyRow>
        <PropertyRow label="Letter Spacing">
          <input
            type="range"
            min="-2"
            max="10"
            step="0.5"
            value={data.letterSpacing || 0}
            onChange={(e) => updateData({ letterSpacing: parseFloat(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {(data.letterSpacing || 0).toFixed(1)}px
          </span>
        </PropertyRow>
        <PropertyRow label="Text Opacity">
          <input
            type="range"
            min="0"
            max="100"
            value={(data.textOpacity !== undefined ? data.textOpacity : 1) * 100}
            onChange={(e) => updateData({ textOpacity: parseInt(e.target.value) / 100 })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {Math.round((data.textOpacity !== undefined ? data.textOpacity : 1) * 100)}%
          </span>
        </PropertyRow>
      </Section>
    </div>
  )
}

// ARRANGE TAB
function ArrangeTab({ node, allNodes, updateNode, onSelectNode, onUngroupNodes }: any) {
  const style = node.style || {}
  const data = node.data || {}

  // Get parent node if this is a child (using React Flow's parentId)
  const parentNode = node.parentId
    ? allNodes.find((n: Node) => n.id === node.parentId)
    : null

  // Get child nodes if this is a parent (find nodes where their parentId === this node's id)
  const childNodes = allNodes.filter((n: Node) => n.parentId === node.id)

  const isParent = childNodes.length > 0
  const isChild = !!parentNode
  const isLocked = data.locked === true

  return (
    <div className="tab-content">
      {/* Size & Position Section */}
      <Section title="Size & Position">
        <PropertyRow label="Current Size">
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Width</div>
              <input
                type="text"
                value={`${Math.round(node.measured?.width || node.width || 120)}px`}
                readOnly
                className="text-input"
                style={{ background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Height</div>
              <input
                type="text"
                value={`${Math.round(node.measured?.height || node.height || 120)}px`}
                readOnly
                className="text-input"
                style={{ background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
              />
            </div>
          </div>
        </PropertyRow>

        <PropertyRow label="Position">
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>X</div>
              <input
                type="text"
                value={`${Math.round(node.position.x)}px`}
                readOnly
                className="text-input"
                style={{ background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Y</div>
              <input
                type="text"
                value={`${Math.round(node.position.y)}px`}
                readOnly
                className="text-input"
                style={{ background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }}
              />
            </div>
          </div>
        </PropertyRow>

        <PropertyRow label="Minimum Size">
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Min Width</div>
              <input
                type="number"
                value={data.minWidth || 25}
                onChange={(e) => updateNode({ data: { ...data, minWidth: Number(e.target.value) } })}
                className="number-input"
                min={10}
                max={data.maxWidth || 5000}
                placeholder="25"
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Min Height</div>
              <input
                type="number"
                value={data.minHeight || 25}
                onChange={(e) => updateNode({ data: { ...data, minHeight: Number(e.target.value) } })}
                className="number-input"
                min={10}
                max={data.maxHeight || 5000}
                placeholder="25"
              />
            </div>
          </div>
        </PropertyRow>

        <PropertyRow label="Maximum Size">
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Max Width</div>
              <input
                type="number"
                value={data.maxWidth || 1000}
                onChange={(e) => updateNode({ data: { ...data, maxWidth: Number(e.target.value) } })}
                className="number-input"
                min={data.minWidth || 10}
                max={5000}
                placeholder="1000"
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Max Height</div>
              <input
                type="number"
                value={data.maxHeight || 1000}
                onChange={(e) => updateNode({ data: { ...data, maxHeight: Number(e.target.value) } })}
                className="number-input"
                min={data.minHeight || 10}
                max={5000}
                placeholder="1000"
              />
            </div>
          </div>
        </PropertyRow>
      </Section>

      {/* Transform (rotation). Applied to the node's visual content; edges
          attach to the un-rotated bounding box. */}
      <Section title="Transform">
        <PropertyRow label="Angle">
          <div style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'center' }}>
            <input
              type="number"
              min={0}
              max={360}
              value={Math.round(((data.rotation || 0) % 360 + 360) % 360)}
              onChange={(e) => {
                const raw = Number(e.target.value)
                const deg = Number.isFinite(raw) ? ((raw % 360) + 360) % 360 : 0
                updateNode({ data: { ...data, rotation: deg } })
              }}
              className="number-input"
              style={{ width: '70px' }}
            />
            <span style={{ fontSize: '11px', color: '#666' }}>deg</span>
          </div>
        </PropertyRow>
        <PropertyRow label="Rotate">
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={Math.round(((data.rotation || 0) % 360 + 360) % 360)}
            onChange={(e) => updateNode({ data: { ...data, rotation: Number(e.target.value) } })}
            style={{ width: '100%' }}
          />
        </PropertyRow>
        <PropertyRow label="Quick">
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => updateNode({ data: { ...data, rotation: ((((data.rotation || 0) - 15) % 360) + 360) % 360 } })}
              title="Rotate -15°"
            >
              −15°
            </button>
            <button
              onClick={() => updateNode({ data: { ...data, rotation: (((data.rotation || 0) + 15) % 360 + 360) % 360 } })}
              title="Rotate +15°"
            >
              +15°
            </button>
            <button
              onClick={() => updateNode({ data: { ...data, rotation: 0 } })}
              title="Reset rotation"
            >
              Reset
            </button>
          </div>
        </PropertyRow>
      </Section>

      <Section title="Z-Index">
        <PropertyRow label="Layer">
          <input
            type="number"
            value={style.zIndex || 0}
            onChange={(e) => updateNode({ style: { ...style, zIndex: parseInt(e.target.value) } })}
            className="number-input"
          />
        </PropertyRow>
        <PropertyRow label="Actions">
          <div className="button-group-vertical">
            <button onClick={() => updateNode({ style: { ...style, zIndex: (style.zIndex || 0) + 10 } })}>
              Bring Forward
            </button>
            <button onClick={() => updateNode({ style: { ...style, zIndex: (style.zIndex || 0) - 10 } })}>
              Send Backward
            </button>
          </div>
        </PropertyRow>
      </Section>

      <Section title="Lock">
        <PropertyRow label="Locked">
          <div className="lock-toggle">
            <input
              type="checkbox"
              checked={isLocked}
              onChange={(e) => {
                const newLocked = e.target.checked
                updateNode({
                  data: { ...data, locked: newLocked },
                  draggable: !newLocked,
                  selectable: true
                })
              }}
            />
            <span className="lock-label">
              {isLocked ? '🔒 Locked (cannot drag)' : '🔓 Unlocked'}
            </span>
          </div>
        </PropertyRow>
      </Section>

      <Section title="Grouping">
        {isParent && (
          <>
            <PropertyRow label="Role">
              <span className="group-role parent">Parent</span>
            </PropertyRow>
            <div className="group-children">
              <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', display: 'block' }}>
                Children ({childNodes.length}):
              </label>
              {childNodes.map((child: any) => (
                <div key={child.id} className="child-item">
                  <span className="child-name">{child.data?.label || child.id}</span>
                  {onSelectNode && (
                    <button
                      onClick={() => onSelectNode(child.id)}
                      className="select-btn"
                    >
                      Select
                    </button>
                  )}
                </div>
              ))}
            </div>
            {onUngroupNodes && (
              <button onClick={onUngroupNodes} className="ungroup-btn">
                Ungroup All
              </button>
            )}
          </>
        )}

        {isChild && (
          <>
            <PropertyRow label="Role">
              <span className="group-role child">Child</span>
            </PropertyRow>
            <PropertyRow label="Parent">
              <div className="parent-info">
                <span className="parent-name">{parentNode?.data?.label || parentNode?.id}</span>
                {onSelectNode && (
                  <button
                    onClick={() => onSelectNode(parentNode.id)}
                    className="select-btn"
                  >
                    Select
                  </button>
                )}
              </div>
            </PropertyRow>
            {onUngroupNodes && (
              <button onClick={onUngroupNodes} className="ungroup-btn">
                Ungroup from Parent
              </button>
            )}
          </>
        )}

        {!isParent && !isChild && (
          <p className="no-group">Not part of any group</p>
        )}
      </Section>

      <Section title="Transform">
        <PropertyRow label="Draggable">
          <input
            type="checkbox"
            checked={node.draggable !== false}
            onChange={(e) => updateNode({ draggable: e.target.checked })}
          />
        </PropertyRow>
      </Section>
    </div>
  )
}

// STRUCTURED CONTENT EDITORS

// Table Content Editor (for database-table nodes)
function TableContentEditor({ tableContent, updateData }: any) {
  const addColumn = () => {
    const newColumns = [...tableContent.columns, {
      name: 'new_column',
      dataType: 'VARCHAR(255)',
      isPrimaryKey: false,
      isForeignKey: false,
      isNotNull: false,
      isUnique: false
    }]
    updateData({ tableContent: { ...tableContent, columns: newColumns } })
  }

  const removeColumn = (index: number) => {
    const newColumns = tableContent.columns.filter((_: any, i: number) => i !== index)
    updateData({ tableContent: { ...tableContent, columns: newColumns } })
  }

  const updateColumn = (index: number, field: string, value: any) => {
    const newColumns = [...tableContent.columns]
    newColumns[index] = { ...newColumns[index], [field]: value }
    updateData({ tableContent: { ...tableContent, columns: newColumns } })
  }

  // Indexes editor (schema supports tableContent.indexes[] but the UI never exposed it)
  const indexes = tableContent.indexes || []
  const addIndex = () => {
    updateData({ tableContent: { ...tableContent, indexes: [...indexes, { name: 'idx_new', columns: [], type: 'BTREE' }] } })
  }
  const removeIndex = (i: number) => {
    updateData({ tableContent: { ...tableContent, indexes: indexes.filter((_: any, idx: number) => idx !== i) } })
  }
  const updateIndex = (i: number, field: string, value: any) => {
    const next = [...indexes]
    next[i] = { ...next[i], [field]: value }
    updateData({ tableContent: { ...tableContent, indexes: next } })
  }

  return (
    <div>
      <Section title="Table Name">
        <input
          type="text"
          value={tableContent.tableName}
          onChange={(e) => updateData({ tableContent: { ...tableContent, tableName: e.target.value } })}
          className="text-input"
        />
      </Section>

      <Section title="Columns">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={addColumn} className="add-btn" style={{ width: '100%', padding: '8px', background: '#0891b2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Column
          </button>
        </div>
        {tableContent.columns.map((column: any, index: number) => (
          <div key={index} style={{ marginBottom: '12px', padding: '12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '13px' }}>Column {index + 1}</strong>
              <button onClick={() => removeColumn(index)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
                Remove
              </button>
            </div>
            <PropertyRow label="Name">
              <input
                type="text"
                value={column.name}
                onChange={(e) => updateColumn(index, 'name', e.target.value)}
                className="text-input"
              />
            </PropertyRow>
            <PropertyRow label="Data Type">
              <input
                type="text"
                value={column.dataType}
                onChange={(e) => updateColumn(index, 'dataType', e.target.value)}
                className="text-input"
              />
            </PropertyRow>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <input type="checkbox" checked={column.isPrimaryKey || false} onChange={(e) => updateColumn(index, 'isPrimaryKey', e.target.checked)} style={{ marginRight: '4px' }} />
                Primary Key
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <input type="checkbox" checked={column.isForeignKey || false} onChange={(e) => updateColumn(index, 'isForeignKey', e.target.checked)} style={{ marginRight: '4px' }} />
                Foreign Key
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <input type="checkbox" checked={column.isNotNull || false} onChange={(e) => updateColumn(index, 'isNotNull', e.target.checked)} style={{ marginRight: '4px' }} />
                Not Null
              </label>
              <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px' }}>
                <input type="checkbox" checked={column.isUnique || false} onChange={(e) => updateColumn(index, 'isUnique', e.target.checked)} style={{ marginRight: '4px' }} />
                Unique
              </label>
            </div>
            <div style={{ marginTop: '8px' }}>
              <PropertyRow label="Default">
                <input
                  type="text"
                  value={column.defaultValue || ''}
                  onChange={(e) => updateColumn(index, 'defaultValue', e.target.value)}
                  className="text-input"
                  placeholder="e.g. CURRENT_TIMESTAMP"
                />
              </PropertyRow>
              <PropertyRow label="Description">
                <input
                  type="text"
                  value={column.description || ''}
                  onChange={(e) => updateColumn(index, 'description', e.target.value)}
                  className="text-input"
                />
              </PropertyRow>
            </div>
          </div>
        ))}
      </Section>

      <Section title="Indexes">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={addIndex} className="add-btn" style={{ width: '100%', padding: '8px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Index
          </button>
        </div>
        {indexes.map((idx: any, i: number) => (
          <div key={i} style={{ marginBottom: '8px', padding: '10px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <strong style={{ fontSize: '12px' }}>Index {i + 1}</strong>
              <button onClick={() => removeIndex(i)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>Remove</button>
            </div>
            <PropertyRow label="Name">
              <input type="text" value={idx.name || ''} onChange={(e) => updateIndex(i, 'name', e.target.value)} className="text-input" />
            </PropertyRow>
            <PropertyRow label="Columns">
              <input
                type="text"
                value={(idx.columns || []).join(', ')}
                onChange={(e) => updateIndex(i, 'columns', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))}
                className="text-input"
                placeholder="col_a, col_b"
              />
            </PropertyRow>
          </div>
        ))}
      </Section>
    </div>
  )
}

// API Resource Editor (for api-resource nodes)
function APIResourceEditor({ resourceContent, updateData }: any) {
  const addMethod = () => {
    const newMethods = [...resourceContent.methods, {
      type: 'GET',
      signature: 'newMethod()',
      description: ''
    }]
    updateData({ resourceContent: { ...resourceContent, methods: newMethods } })
  }

  const removeMethod = (index: number) => {
    const newMethods = resourceContent.methods.filter((_: any, i: number) => i !== index)
    updateData({ resourceContent: { ...resourceContent, methods: newMethods } })
  }

  const updateMethod = (index: number, field: string, value: any) => {
    const newMethods = [...resourceContent.methods]
    newMethods[index] = { ...newMethods[index], [field]: value }
    updateData({ resourceContent: { ...resourceContent, methods: newMethods } })
  }

  // Attributes editor (schema supports resourceContent.attributes[] but the UI never exposed it)
  const attributes: string[] = resourceContent.attributes || []
  const setAttributes = (next: string[]) => updateData({ resourceContent: { ...resourceContent, attributes: next } })

  return (
    <div>
      <Section title="Resource Name">
        <input
          type="text"
          value={resourceContent.resourceName}
          onChange={(e) => updateData({ resourceContent: { ...resourceContent, resourceName: e.target.value } })}
          className="text-input"
        />
      </Section>

      <Section title="Stereotype">
        <input
          type="text"
          value={resourceContent.stereotype}
          onChange={(e) => updateData({ resourceContent: { ...resourceContent, stereotype: e.target.value } })}
          className="text-input"
          placeholder="resource, controller, service..."
        />
      </Section>

      <Section title="Attributes">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={() => setAttributes([...attributes, 'newField'])} className="add-btn" style={{ width: '100%', padding: '8px', background: '#0891b2', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Attribute
          </button>
        </div>
        {attributes.map((attr, i) => (
          <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
            <input
              type="text"
              value={attr}
              onChange={(e) => setAttributes(attributes.map((a, j) => (j === i ? e.target.value : a)))}
              className="text-input"
              style={{ flex: 1 }}
            />
            <button onClick={() => setAttributes(attributes.filter((_, j) => j !== i))} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        ))}
      </Section>

      <Section title="Methods">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={addMethod} className="add-btn" style={{ width: '100%', padding: '8px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Method
          </button>
        </div>
        {resourceContent.methods.map((method: any, index: number) => (
          <div key={index} style={{ marginBottom: '12px', padding: '12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '13px' }}>Method {index + 1}</strong>
              <button onClick={() => removeMethod(index)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
                Remove
              </button>
            </div>
            <PropertyRow label="HTTP Method">
              <select
                value={method.type}
                onChange={(e) => updateMethod(index, 'type', e.target.value)}
                style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
            </PropertyRow>
            <PropertyRow label="Signature">
              <input
                type="text"
                value={method.signature}
                onChange={(e) => updateMethod(index, 'signature', e.target.value)}
                className="text-input"
                placeholder="methodName(params)"
              />
            </PropertyRow>
          </div>
        ))}
      </Section>
    </div>
  )
}

// UML Class Editor (for uml-class nodes)
function UMLClassEditor({ classContent, updateData }: any) {
  const attributes: any[] = classContent.attributes || []
  const methods: any[] = classContent.methods || []
  const VIS = ['+', '-', '#', '~']

  const setAttributes = (next: any[]) => updateData({ classContent: { ...classContent, attributes: next } })
  const setMethods = (next: any[]) => updateData({ classContent: { ...classContent, methods: next } })

  return (
    <div>
      <Section title="Class Name">
        <input
          type="text"
          value={classContent.className || ''}
          onChange={(e) => updateData({ classContent: { ...classContent, className: e.target.value } })}
          className="text-input"
        />
      </Section>

      <Section title="Stereotype">
        <input
          type="text"
          value={classContent.stereotype || ''}
          onChange={(e) => updateData({ classContent: { ...classContent, stereotype: e.target.value || undefined } })}
          className="text-input"
          placeholder="interface, abstract, enumeration..."
        />
      </Section>

      <Section title="Generics">
        <input
          type="text"
          value={classContent.generics || ''}
          onChange={(e) => updateData({ classContent: { ...classContent, generics: e.target.value || undefined } })}
          className="text-input"
          placeholder="<T>, <K, V>"
        />
      </Section>

      <Section title="Attributes">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={() => setAttributes([...attributes, { visibility: '+', name: 'field', type: 'string' }])} className="add-btn" style={{ width: '100%', padding: '8px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Attribute
          </button>
        </div>
        {attributes.map((attr, i) => (
          <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <select
              value={attr.visibility || '+'}
              onChange={(e) => setAttributes(attributes.map((a, j) => (j === i ? { ...a, visibility: e.target.value } : a)))}
              style={{ width: '46px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              title="Visibility"
            >
              {VIS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <input
              type="text"
              value={attr.name || ''}
              onChange={(e) => setAttributes(attributes.map((a, j) => (j === i ? { ...a, name: e.target.value } : a)))}
              className="text-input"
              style={{ flex: 1 }}
              placeholder="name"
            />
            <input
              type="text"
              value={attr.type || ''}
              onChange={(e) => setAttributes(attributes.map((a, j) => (j === i ? { ...a, type: e.target.value || undefined } : a)))}
              className="text-input"
              style={{ flex: 1 }}
              placeholder="type"
            />
            <button onClick={() => setAttributes(attributes.filter((_, j) => j !== i))} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        ))}
      </Section>

      <Section title="Methods">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={() => setMethods([...methods, { visibility: '+', signature: 'method(): void' }])} className="add-btn" style={{ width: '100%', padding: '8px', background: '#6366f1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Method
          </button>
        </div>
        {methods.map((method, i) => (
          <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
            <select
              value={method.visibility || '+'}
              onChange={(e) => setMethods(methods.map((m, j) => (j === i ? { ...m, visibility: e.target.value } : m)))}
              style={{ width: '46px', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
              title="Visibility"
            >
              {VIS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <input
              type="text"
              value={method.signature || ''}
              onChange={(e) => setMethods(methods.map((m, j) => (j === i ? { ...m, signature: e.target.value } : m)))}
              className="text-input"
              style={{ flex: 1 }}
              placeholder="getName(): string"
            />
            <button onClick={() => setMethods(methods.map((m, j) => (j === i ? { ...m, isAbstract: !m.isAbstract } : m)))} title="Toggle abstract (italic)" style={{ background: method.isAbstract ? '#6366f1' : '#e5e7eb', color: method.isAbstract ? 'white' : '#374151', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', fontStyle: 'italic', cursor: 'pointer' }}>
              abs
            </button>
            <button onClick={() => setMethods(methods.filter((_, j) => j !== i))} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
              ✕
            </button>
          </div>
        ))}
      </Section>
    </div>
  )
}

// C4 Model Editor (for c4-node nodes)
function C4Editor({ c4Content, updateData }: any) {
  const ELEMENTS = ['person', 'system', 'container', 'component']
  const set = (patch: any) => updateData({ c4Content: { ...c4Content, ...patch } })
  return (
    <div>
      <Section title="Element Type">
        <select
          value={c4Content.elementType || 'system'}
          onChange={(e) => set({ elementType: e.target.value })}
          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
        >
          {ELEMENTS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </Section>
      <Section title="Name">
        <input type="text" value={c4Content.name || ''} onChange={(e) => set({ name: e.target.value })} className="text-input" />
      </Section>
      <Section title="Technology">
        <input type="text" value={c4Content.technology || ''} onChange={(e) => set({ technology: e.target.value || undefined })} className="text-input" placeholder="Spring Boot, PostgreSQL..." />
      </Section>
      <Section title="Description">
        <input type="text" value={c4Content.description || ''} onChange={(e) => set({ description: e.target.value || undefined })} className="text-input" placeholder="responsibility / purpose" />
      </Section>
      <Section title="External">
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <input type="checkbox" checked={!!c4Content.external} onChange={(e) => set({ external: e.target.checked || undefined })} />
          Outside the system boundary (greyed)
        </label>
      </Section>
    </div>
  )
}

// State Machine Editor (for state-node nodes)
function StateEditor({ stateContent, updateData }: any) {
  const internal: string[] = stateContent.internalTransitions || []
  const setInternal = (next: string[]) => updateData({ stateContent: { ...stateContent, internalTransitions: next } })
  const KINDS = ['simple', 'composite', 'initial', 'final', 'choice', 'fork', 'join']
  const isBox = !stateContent.kind || stateContent.kind === 'simple' || stateContent.kind === 'composite'

  return (
    <div>
      <Section title="State Name">
        <input
          type="text"
          value={stateContent.stateName || ''}
          onChange={(e) => updateData({ stateContent: { ...stateContent, stateName: e.target.value } })}
          className="text-input"
        />
      </Section>

      <Section title="Kind">
        <select
          value={stateContent.kind || 'simple'}
          onChange={(e) => updateData({ stateContent: { ...stateContent, kind: e.target.value } })}
          style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db' }}
        >
          {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
      </Section>

      {isBox && (
        <>
          <Section title="Description">
            <input
              type="text"
              value={stateContent.description || ''}
              onChange={(e) => updateData({ stateContent: { ...stateContent, description: e.target.value || undefined } })}
              className="text-input"
              placeholder="optional summary"
            />
          </Section>

          <Section title="Internal Transitions">
            <div style={{ marginBottom: '8px' }}>
              <button onClick={() => setInternal([...internal, 'entry / action'])} className="add-btn" style={{ width: '100%', padding: '8px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                + Add Action
              </button>
            </div>
            {internal.map((t, i) => (
              <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                <input
                  type="text"
                  value={t}
                  onChange={(e) => setInternal(internal.map((x, j) => (j === i ? e.target.value : x)))}
                  className="text-input"
                  style={{ flex: 1 }}
                  placeholder="entry / openDoor"
                />
                <button onClick={() => setInternal(internal.filter((_, j) => j !== i))} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
                  ✕
                </button>
              </div>
            ))}
          </Section>
        </>
      )}
    </div>
  )
}

// API Endpoint Editor (for api-endpoint nodes)
function APIEndpointEditor({ endpointContent, updateData }: any) {
  const addEndpointGroup = () => {
    const newEndpoints = [...endpointContent.endpoints, {
      method: 'GET',
      items: []
    }]
    updateData({ endpointContent: { ...endpointContent, endpoints: newEndpoints } })
  }

  const removeEndpointGroup = (index: number) => {
    const newEndpoints = endpointContent.endpoints.filter((_: any, i: number) => i !== index)
    updateData({ endpointContent: { ...endpointContent, endpoints: newEndpoints } })
  }

  const addEndpointItem = (groupIndex: number) => {
    const newEndpoints = [...endpointContent.endpoints]
    newEndpoints[groupIndex] = {
      ...newEndpoints[groupIndex],
      items: [...newEndpoints[groupIndex].items, { path: '/api/new', description: 'New endpoint' }]
    }
    updateData({ endpointContent: { ...endpointContent, endpoints: newEndpoints } })
  }

  const removeEndpointItem = (groupIndex: number, itemIndex: number) => {
    const newEndpoints = [...endpointContent.endpoints]
    newEndpoints[groupIndex] = {
      ...newEndpoints[groupIndex],
      items: newEndpoints[groupIndex].items.filter((_: any, i: number) => i !== itemIndex)
    }
    updateData({ endpointContent: { ...endpointContent, endpoints: newEndpoints } })
  }

  const updateEndpointItem = (groupIndex: number, itemIndex: number, field: string, value: any) => {
    const newEndpoints = [...endpointContent.endpoints]
    const newItems = [...newEndpoints[groupIndex].items]
    newItems[itemIndex] = { ...newItems[itemIndex], [field]: value }
    newEndpoints[groupIndex] = { ...newEndpoints[groupIndex], items: newItems }
    updateData({ endpointContent: { ...endpointContent, endpoints: newEndpoints } })
  }

  return (
    <div>
      <Section title="Entity Name">
        <input
          type="text"
          value={endpointContent.entityName}
          onChange={(e) => updateData({ endpointContent: { ...endpointContent, entityName: e.target.value } })}
          className="text-input"
        />
      </Section>

      <Section title="Description">
        <textarea
          value={endpointContent.description}
          onChange={(e) => updateData({ endpointContent: { ...endpointContent, description: e.target.value } })}
          placeholder="API description..."
          rows={2}
          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '13px' }}
        />
      </Section>

      <Section title="Endpoint Groups">
        <div style={{ marginBottom: '8px' }}>
          <button onClick={addEndpointGroup} className="add-btn" style={{ width: '100%', padding: '8px', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            + Add Method Group
          </button>
        </div>
        {endpointContent.endpoints.map((group: any, groupIndex: number) => (
          <div key={groupIndex} style={{ marginBottom: '16px', padding: '12px', background: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <select
                value={group.method}
                onChange={(e) => {
                  const newEndpoints = [...endpointContent.endpoints]
                  newEndpoints[groupIndex] = { ...newEndpoints[groupIndex], method: e.target.value }
                  updateData({ endpointContent: { ...endpointContent, endpoints: newEndpoints } })
                }}
                style={{ padding: '6px', borderRadius: '4px', border: '1px solid #d1d5db', fontWeight: 'bold' }}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </select>
              <button onClick={() => removeEndpointGroup(groupIndex)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
                Remove Group
              </button>
            </div>
            <div style={{ marginBottom: '8px' }}>
              <button onClick={() => addEndpointItem(groupIndex)} style={{ width: '100%', padding: '6px', background: 'white', color: '#8b5cf6', border: '1px solid #8b5cf6', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
                + Add Endpoint
              </button>
            </div>
            {group.items.map((item: any, itemIndex: number) => (
              <div key={itemIndex} style={{ marginBottom: '8px', padding: '8px', background: 'white', borderRadius: '4px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '4px' }}>
                  <input
                    type="text"
                    value={item.path}
                    onChange={(e) => updateEndpointItem(groupIndex, itemIndex, 'path', e.target.value)}
                    placeholder="/api/path"
                    style={{ flex: 1, padding: '4px 6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', fontFamily: 'JetBrains Mono, monospace' }}
                  />
                  <button onClick={() => removeEndpointItem(groupIndex, itemIndex)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
                    ×
                  </button>
                </div>
                <input
                  type="text"
                  value={item.description}
                  onChange={(e) => updateEndpointItem(groupIndex, itemIndex, 'description', e.target.value)}
                  placeholder="Description..."
                  style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px' }}
                />
              </div>
            ))}
          </div>
        ))}
      </Section>
    </div>
  )
}

// DATA TAB
function DataTab({ node, updateData }: any) {
  const data = node.data || {}
  const isStructuredNode = node.type === 'database-table' || node.type === 'api-resource' || node.type === 'api-endpoint' || node.type === 'uml-class' || node.type === 'state-node' || node.type === 'c4-node'

  return (
    <div className="tab-content">
      {/* Structured Content Editors */}
      {node.type === 'database-table' && data.tableContent && (
        <TableContentEditor tableContent={data.tableContent} updateData={updateData} />
      )}
      {node.type === 'api-resource' && data.resourceContent && (
        <APIResourceEditor resourceContent={data.resourceContent} updateData={updateData} />
      )}
      {node.type === 'api-endpoint' && data.endpointContent && (
        <APIEndpointEditor endpointContent={data.endpointContent} updateData={updateData} />
      )}
      {node.type === 'uml-class' && data.classContent && (
        <UMLClassEditor classContent={data.classContent} updateData={updateData} />
      )}
      {node.type === 'state-node' && data.stateContent && (
        <StateEditor stateContent={data.stateContent} updateData={updateData} />
      )}
      {node.type === 'c4-node' && data.c4Content && (
        <C4Editor c4Content={data.c4Content} updateData={updateData} />
      )}

      {/* Standard Metadata (for all nodes) */}
      {!isStructuredNode && (
        <>
          <Section title="Metadata">
            <PropertyRow label="ID">
              <input type="text" value={node.id} disabled className="text-input" />
            </PropertyRow>
            <PropertyRow label="Type">
              <input
                type="text"
                value={node.type || 'universal'}
                disabled
                className="text-input"
              />
            </PropertyRow>
            <PropertyRow label="Node Type">
              <select
                value={data.nodeType || 'component'}
                onChange={(e) => updateData({ nodeType: e.target.value })}
              >
                <option value="component">Component</option>
                <option value="service">Service</option>
                <option value="database">Database</option>
                <option value="api">API</option>
                <option value="cloud">Cloud</option>
                <option value="storage">Storage</option>
              </select>
            </PropertyRow>
          </Section>

          <Section title="Description">
            <textarea
              value={data.description || ''}
              onChange={(e) => updateData({ description: e.target.value })}
              placeholder="Add a description..."
              rows={4}
            />
          </Section>
        </>
      )}
    </div>
  )
}

// EDGE PROPERTIES TAB
function EdgePropertiesTab({ edge, onEdgeUpdate }: any) {
  // Local state for real-time UI updates (fixes slider display issue)
  const [localEdge, setLocalEdge] = useState(edge)

  // Update local edge when selection changes
  useEffect(() => {
    setLocalEdge(edge)
  }, [edge])

  const data = localEdge.data || {}

  const updateEdgeData = (dataUpdates: any) => {
    const newData = { ...data, ...dataUpdates }
    const updatedEdge = { ...localEdge, data: newData }
    setLocalEdge(updatedEdge)
    onEdgeUpdate(localEdge.id, { data: newData })
  }

  const handleRoutingPreferenceChange = (preference: 'auto' | 'vertical' | 'horizontal') => {
    const newData = { ...data, handlePreference: preference }
    const updatedEdge = {
      ...localEdge,
      data: newData,
      sourceHandle: undefined,
      targetHandle: undefined,
    }

    // Clearing handles lets the automatic assignment rerun with the new preference.
    setLocalEdge(updatedEdge)
    onEdgeUpdate(localEdge.id, {
      data: newData,
      sourceHandle: undefined,
      targetHandle: undefined,
    })
  }

  return (
    <div className="tab-content">
      {/* Label */}
      <Section title="Label">
        <PropertyRow label="Label Mode">
          <select
            value={data.labelMode || 'default'}
            onChange={(e) => updateEdgeData({ labelMode: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="default">Default (Label + Protocol)</option>
            <option value="split">Split (Label / Text)</option>
            <option value="markdown">Markdown</option>
          </select>
        </PropertyRow>

        {/* Default mode: Label + Protocol */}
        {(data.labelMode || 'default') === 'default' && (
          <>
            <PropertyRow label="Label">
              <input
                type="text"
                value={data.label || localEdge.label || ''}
                onChange={(e) => {
                  const updatedEdge = { ...localEdge, label: e.target.value }
                  setLocalEdge(updatedEdge)
                  onEdgeUpdate(localEdge.id, { label: e.target.value })
                }}
                placeholder="Add label..."
                style={{
                  flex: 1,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
            </PropertyRow>
            <PropertyRow label="Protocol">
              <input
                type="text"
                value={data.protocol || ''}
                onChange={(e) => updateEdgeData({ protocol: e.target.value })}
                placeholder="REST, gRPC, etc."
                style={{
                  flex: 1,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
            </PropertyRow>
          </>
        )}

        {/* Split mode: Label / Text */}
        {data.labelMode === 'split' && (
          <>
            <PropertyRow label="Label (Top)">
              <input
                type="text"
                value={data.label || localEdge.label || ''}
                onChange={(e) => {
                  const updatedEdge = { ...localEdge, label: e.target.value }
                  setLocalEdge(updatedEdge)
                  onEdgeUpdate(localEdge.id, { label: e.target.value })
                }}
                placeholder="Title text..."
                style={{
                  flex: 1,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
            </PropertyRow>
            <PropertyRow label="Text (Bottom)">
              <textarea
                value={data.labelText || ''}
                onChange={(e) => updateEdgeData({ labelText: e.target.value })}
                placeholder="Additional text..."
                rows={3}
                style={{
                  flex: 1,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
            </PropertyRow>
          </>
        )}

        {/* Markdown mode */}
        {data.labelMode === 'markdown' && (
          <>
            <PropertyRow label="Markdown">
              <textarea
                value={data.labelText || ''}
                onChange={(e) => updateEdgeData({ labelText: e.target.value })}
                placeholder="**Bold** *Italic*&#10;---&#10;# Heading"
                rows={5}
                style={{
                  flex: 1,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
            </PropertyRow>
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              padding: '4px 8px',
              background: '#f9fafb',
              borderRadius: '4px',
              marginBottom: '8px'
            }}>
              <strong>Tip:</strong> **bold** *italic* # heading --- (separator)
            </div>
          </>
        )}

        <PropertyRow label="Font Size">
          <select
            value={data.labelFontSize || 12}
            onChange={(e) => updateEdgeData({ labelFontSize: parseInt(e.target.value) })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="8">8px</option>
            <option value="10">10px</option>
            <option value="12">12px</option>
            <option value="14">14px</option>
            <option value="16">16px</option>
            <option value="18">18px</option>
            <option value="20">20px</option>
            <option value="24">24px</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Background">
          <select
            value={data.labelBackground || 'white'}
            onChange={(e) => updateEdgeData({ labelBackground: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="none">None (Transparent)</option>
            <option value="white">White</option>
            <option value="custom">Custom Color</option>
          </select>
        </PropertyRow>
        {data.labelBackground === 'custom' && (
          <PropertyRow label="Bg Color">
            <input
              type="color"
              value={data.labelBackgroundColor || '#ffffff'}
              onChange={(e) => updateEdgeData({ labelBackgroundColor: e.target.value })}
              style={{ width: '40px', height: '32px', flexShrink: 0 }}
            />
            <input
              type="text"
              value={data.labelBackgroundColor || '#ffffff'}
              onChange={(e) => updateEdgeData({ labelBackgroundColor: e.target.value })}
              placeholder="#ffffff"
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: '13px',
                padding: '6px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px'
              }}
            />
          </PropertyRow>
        )}
        <PropertyRow label="Label Z-Index">
          <input
            type="number"
            min="-100"
            max="1000"
            value={data.labelZIndex !== undefined ? data.labelZIndex : 1}
            onChange={(e) => updateEdgeData({ labelZIndex: parseInt(e.target.value) || 0 })}
            style={{
              flex: 1,
              fontSize: '13px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          />
          <span style={{
            marginLeft: '8px',
            fontSize: '11px',
            color: '#6b7280'
          }}>
            Label layer
          </span>
        </PropertyRow>
      </Section>

      {/* Line Style */}
      <Section title="Line Style">
        <PropertyRow label="Style">
          <select
            value={data.lineStyle || 'solid'}
            onChange={(e) => updateEdgeData({ lineStyle: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
            <option value="longdash">Long Dash</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Width">
          <input
            type="range"
            min="1"
            max="8"
            step="0.5"
            value={data.lineWidth || 2}
            onChange={(e) => updateEdgeData({ lineWidth: parseFloat(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {data.lineWidth || 2}px
          </span>
        </PropertyRow>
        <PropertyRow label="Line Cap">
          <select
            value={data.lineCap || 'butt'}
            onChange={(e) => updateEdgeData({ lineCap: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="butt">Flat</option>
            <option value="round">Round</option>
            <option value="square">Square</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Line Join">
          <select
            value={data.lineJoin || 'miter'}
            onChange={(e) => updateEdgeData({ lineJoin: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="miter">Miter (Sharp)</option>
            <option value="round">Round</option>
            <option value="bevel">Bevel</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Animated">
          <input
            type="checkbox"
            checked={localEdge.animated === true}
            onChange={(e) => {
              const updatedEdge = { ...localEdge, animated: e.target.checked }
              setLocalEdge(updatedEdge)
              onEdgeUpdate(localEdge.id, { animated: e.target.checked })
            }}
          />
          <span style={{ marginLeft: '8px', fontSize: '13px' }}>
            {localEdge.animated ? 'Flowing animation' : 'Static'}
          </span>
        </PropertyRow>
      </Section>

      {/* Connector Routing */}
      <Section title="Connector Routing">
        <PropertyRow label="Connection">
          <select
            value={data.fixedConnection ? 'fixed' : 'floating'}
            onChange={(e) =>
              updateEdgeData({ fixedConnection: e.target.value === 'fixed' })
            }
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="floating">Floating (auto)</option>
            <option value="fixed">Fixed (handles)</option>
          </select>
        </PropertyRow>
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '-4px',
          marginBottom: '8px',
          lineHeight: 1.4
        }}>
          Floating attaches to the nearest node boundary; Fixed pins the edge to its assigned connection handles.
        </div>
        <PropertyRow label="Routing">
          <select
            value={localEdge.type === 'smart' ? 'smart' : 'custom'}
            onChange={(e) => {
              const nextType = e.target.value === 'smart' ? 'smart' : 'custom'
              setLocalEdge({ ...localEdge, type: nextType })
              onEdgeUpdate(localEdge.id, { type: nextType })
            }}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="custom">Default</option>
            <option value="smart">Smart (Avoid Obstacles)</option>
          </select>
        </PropertyRow>
        {localEdge.type !== 'smart' && (
          <PropertyRow label="Style">
            <select
              value={data.pathStyle || 'smoothstep'}
              onChange={(e) => updateEdgeData({ pathStyle: e.target.value })}
              style={{
                flex: 1,
                padding: '6px 8px',
                fontSize: '13px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="straight">Straight</option>
              <option value="bezier">Curved</option>
              <option value="smoothstep">Stepped</option>
            </select>
          </PropertyRow>
        )}
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '-4px',
          marginBottom: '8px',
          lineHeight: 1.4
        }}>
          Style: Straight points the arrow dead-on into the node, Curved is a smooth bezier, Stepped is orthogonal elbows. Routing = Smart adds obstacle avoidance.
        </div>
        <PropertyRow label="Preference">
          <select
            value={data.handlePreference || 'auto'}
            onChange={(e) => handleRoutingPreferenceChange(e.target.value as 'auto' | 'vertical' | 'horizontal')}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="auto">Auto (Smart)</option>
            <option value="vertical">Vertical bias</option>
            <option value="horizontal">Horizontal bias</option>
          </select>
        </PropertyRow>
        <div style={{
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '-4px',
          marginBottom: '8px',
          lineHeight: 1.4
        }}>
          Guides the connector to prefer top/bottom joins for vertical flows or side joins for horizontal layouts.
        </div>
      </Section>

      {/* Color */}
      <Section title="Color">
        <PropertyRow label="Stroke">
          <input
            type="color"
            value={data.color || '#b1b1b7'}
            onChange={(e) => updateEdgeData({ color: e.target.value })}
            style={{ width: '40px', height: '32px', flexShrink: 0 }}
          />
          <input
            type="text"
            value={data.color || '#b1b1b7'}
            onChange={(e) => updateEdgeData({ color: e.target.value })}
            placeholder="#b1b1b7"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: '13px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          />
        </PropertyRow>
        <PropertyRow label="Opacity">
          <input
            type="range"
            min="0"
            max="100"
            value={(data.opacity !== undefined ? data.opacity : 1) * 100}
            onChange={(e) => updateEdgeData({ opacity: parseInt(e.target.value) / 100 })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {Math.round((data.opacity !== undefined ? data.opacity : 1) * 100)}%
          </span>
        </PropertyRow>
      </Section>

      {/* Shadow */}
      <Section title="Shadow">
        <PropertyRow label="Enable">
          <input
            type="checkbox"
            checked={data.shadowEnabled === true}
            onChange={(e) => updateEdgeData({ shadowEnabled: e.target.checked })}
          />
          <span style={{ marginLeft: '8px', fontSize: '13px' }}>
            {data.shadowEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </PropertyRow>
        {data.shadowEnabled && (
          <>
            <PropertyRow label="Color">
              <input
                type="color"
                value={data.shadowColor || '#000000'}
                onChange={(e) => updateEdgeData({ shadowColor: e.target.value })}
                style={{ width: '40px', height: '32px', flexShrink: 0 }}
              />
              <input
                type="text"
                value={data.shadowColor || '#000000'}
                onChange={(e) => updateEdgeData({ shadowColor: e.target.value })}
                placeholder="#000000"
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '13px',
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px'
                }}
              />
            </PropertyRow>
            <PropertyRow label="Blur">
              <input
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={data.shadowBlur || 3}
                onChange={(e) => updateEdgeData({ shadowBlur: parseFloat(e.target.value) })}
                style={{ flex: 1 }}
              />
              <span style={{
                minWidth: '45px',
                textAlign: 'right',
                fontSize: '13px',
                color: '#374151'
              }}>
                {data.shadowBlur || 3}px
              </span>
            </PropertyRow>
            <PropertyRow label="Offset X">
              <input
                type="range"
                min="-10"
                max="10"
                step="0.5"
                value={data.shadowOffsetX || 2}
                onChange={(e) => updateEdgeData({ shadowOffsetX: parseFloat(e.target.value) })}
                style={{ flex: 1 }}
              />
              <span style={{
                minWidth: '45px',
                textAlign: 'right',
                fontSize: '13px',
                color: '#374151'
              }}>
                {data.shadowOffsetX || 2}px
              </span>
            </PropertyRow>
            <PropertyRow label="Offset Y">
              <input
                type="range"
                min="-10"
                max="10"
                step="0.5"
                value={data.shadowOffsetY || 2}
                onChange={(e) => updateEdgeData({ shadowOffsetY: parseFloat(e.target.value) })}
                style={{ flex: 1 }}
              />
              <span style={{
                minWidth: '45px',
                textAlign: 'right',
                fontSize: '13px',
                color: '#374151'
              }}>
                {data.shadowOffsetY || 2}px
              </span>
            </PropertyRow>
          </>
        )}
      </Section>

      {/* Arrows */}
      <Section title="Arrows">
        <PropertyRow label="Start Arrow">
          <select
            value={data.arrowStart || 'none'}
            onChange={(e) => updateEdgeData({ arrowStart: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="none">None</option>
            <option value="arrow">Arrow (Filled)</option>
            <option value="open-arrow">Arrow (Open)</option>
            <option value="triangle">Triangle</option>
            <option value="diamond">Diamond</option>
            <option value="circle">Circle (Hollow)</option>
            <option value="dot">Dot (Filled)</option>
            <option value="block">Block</option>
            <option value="line">Line (Bar)</option>
            <option value="slash">Slash</option>
            <option value="cross">Cross (X)</option>
          </select>
        </PropertyRow>
        <PropertyRow label="End Arrow">
          <select
            value={data.arrowEnd || 'arrow'}
            onChange={(e) => updateEdgeData({ arrowEnd: e.target.value })}
            style={{
              flex: 1,
              padding: '6px 8px',
              fontSize: '13px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="none">None</option>
            <option value="arrow">Arrow (Filled)</option>
            <option value="open-arrow">Arrow (Open)</option>
            <option value="triangle">Triangle</option>
            <option value="diamond">Diamond</option>
            <option value="circle">Circle (Hollow)</option>
            <option value="dot">Dot (Filled)</option>
            <option value="block">Block</option>
            <option value="line">Line (Bar)</option>
            <option value="slash">Slash</option>
            <option value="cross">Cross (X)</option>
          </select>
        </PropertyRow>
        <PropertyRow label="Arrow Size">
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={data.arrowSize || 1}
            onChange={(e) => updateEdgeData({ arrowSize: parseFloat(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '45px',
            textAlign: 'right',
            fontSize: '13px',
            color: '#374151'
          }}>
            {((data.arrowSize || 1) * 100).toFixed(0)}%
          </span>
        </PropertyRow>
        <PropertyRow label="Arrow Angle">
          <input
            type="range"
            min="0"
            max="360"
            step="1"
            value={data.targetAngle ?? 90}
            onChange={(e) => updateEdgeData({ targetAngle: parseInt(e.target.value) })}
            style={{ flex: 1 }}
          />
          <span style={{
            minWidth: '52px',
            textAlign: 'right',
            fontSize: '13px',
            color: data.targetAngle != null ? '#2563eb' : '#9ca3af'
          }}>
            {data.targetAngle != null ? `${data.targetAngle}°` : 'Auto'}
          </span>
        </PropertyRow>
        {data.targetAngle != null && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-4px', marginBottom: '6px' }}>
            <button
              onClick={() => updateEdgeData({ targetAngle: undefined })}
              style={{ fontSize: '11px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              Reset to auto
            </button>
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '-2px', marginBottom: '8px', lineHeight: 1.4 }}>
          Spin the arrowhead around the target node: 0° right, 90° down, 180° left, 270° up. Auto attaches to the side facing the source.
        </div>
        <PropertyRow label="Bidirectional">
          <input
            type="checkbox"
            checked={data.bidirectional === true}
            onChange={(e) => updateEdgeData({ bidirectional: e.target.checked })}
          />
          <span style={{ marginLeft: '8px', fontSize: '13px' }}>
            {data.bidirectional ? 'Enabled' : 'Disabled'}
          </span>
        </PropertyRow>
      </Section>

      {/* Metadata */}
      <Section title="Metadata">
        <PropertyRow label="Edge Z-Index">
          <input
            type="number"
            min="-100"
            max="10000"
            value={data.zIndex !== undefined ? data.zIndex : 0}
            onChange={(e) => {
              const zIndexValue = parseInt(e.target.value) || 0
              // Update both data.zIndex AND edge-level zIndex
              const updatedEdge = { ...localEdge, zIndex: zIndexValue, data: { ...data, zIndex: zIndexValue } }
              setLocalEdge(updatedEdge)
              onEdgeUpdate(localEdge.id, { zIndex: zIndexValue, data: { ...data, zIndex: zIndexValue } })
            }}
            style={{
              flex: 1,
              fontSize: '13px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px'
            }}
          />
          <span style={{
            marginLeft: '8px',
            fontSize: '11px',
            color: '#6b7280'
          }}>
            Edge layer
          </span>
        </PropertyRow>
        <PropertyRow label="ID">
          <input
            type="text"
            value={localEdge.id}
            disabled
            style={{
              flex: 1,
              fontSize: '13px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: '#f9fafb',
              color: '#6b7280'
            }}
          />
        </PropertyRow>
        <PropertyRow label="Source">
          <input
            type="text"
            value={localEdge.source}
            disabled
            style={{
              flex: 1,
              fontSize: '13px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: '#f9fafb',
              color: '#6b7280'
            }}
          />
        </PropertyRow>
        <PropertyRow label="Target">
          <input
            type="text"
            value={localEdge.target}
            disabled
            style={{
              flex: 1,
              fontSize: '13px',
              padding: '6px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: '#f9fafb',
              color: '#6b7280'
            }}
          />
        </PropertyRow>
      </Section>
    </div>
  )
}

// HELPER COMPONENTS
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div className="section">
      <div
        className="section-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <h4 className="section-title">{title}</h4>
      </div>
      {isOpen && <div className="section-content">{children}</div>}
    </div>
  )
}

function PropertyRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="property-row-v2">
      <label className="property-label">{label}</label>
      <div className="property-controls-v2">{children}</div>
    </div>
  )
}
